import { Profile } from "@moe/priv/model/profile";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@web/components/LoadingSpinner"; // Import the LoadingSpinner component
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { AnimatedList } from "./CardList";
import { FollowCard } from "./FollowCard";

type Tab = "follower" | "following";

interface Props {
  profile: Profile;
}

export function FollowModal({ profile }: Props) {
  const [tab, setTab] = useState<Tab>("follower");

  return (
    <div className="flex h-[60rem] w-[42rem] flex-col items-center">
      <div className="scroll-secondary flex w-full flex-col space-y-2 overflow-y-auto px-3">
        <div className="flex flex-row items-center space-x-3">
          <button
            className="group flex items-center justify-between transition duration-200 ease-out hover:brightness-90"
            onClick={() => setTab("follower")}
          >
            <h1 className="text-tx-primary pb-3 pl-4 pt-6 text-2xl font-semibold">Followers</h1>
          </button>

          <h1 className="text-tx-primary cursor-default pb-3 pl-4 pt-6 text-2xl font-semibold">|</h1>

          <button
            className="group flex items-center justify-between transition duration-200 ease-out hover:brightness-90"
            onClick={() => setTab("following")}
          >
            <h1 className="text-tx-primary pb-3 pl-4 pt-6 text-2xl font-semibold">Following</h1>
          </button>
        </div>
        {tab === "follower" ? <FollowerTab profile={profile} /> : <FollowingTab profile={profile} />}
      </div>
    </div>
  );
}

interface FollowersTabProps {
  profile: Profile;
}

function FollowerTab({ profile }: FollowersTabProps) {
  const { access } = useAppContext();
  const { session } = useAppContext();
  const isOwnProfile = profile.id === session?.user.id;

  const { data: followers = [], isPending: isFollowersPending } = useQuery({
    queryKey: ["getFollowers", profile.followers],
    queryFn: () => access.profile.getByID(profile.followers)
  });

  return (
    <AnimatePresence mode="wait">
      {isFollowersPending ? (
        <LoadingSpinner key="loading-followers" />
      ) : followers.length === 0 ? (
        <Empty
          key="empty-followers"
          message={`${isOwnProfile ? "You don't" : "This user doesn't"} have any followers yet...`}
        />
      ) : (
        <AnimatedList
          key="followers-list"
          items={followers}
          renderItem={(follower) => <FollowCard profile={follower} />}
          itemKeyFn={(follower) => follower.id}
        />
      )}
    </AnimatePresence>
  );
}

interface FollowingTabProps {
  profile: Profile;
}

function FollowingTab({ profile }: FollowingTabProps) {
  const { access, session } = useAppContext();
  const isOwnProfile = profile.id === session?.user.id;

  const { data: following = [], isPending: isFollowingPending } = useQuery({
    queryKey: ["getFollowing", profile.following],
    queryFn: () => access.profile.getByID(profile.following)
  });

  return (
    <AnimatePresence mode="wait">
      {isFollowingPending ? (
        <LoadingSpinner key="loading-following" />
      ) : following.length === 0 ? (
        <Empty
          key="empty-following"
          message={`${isOwnProfile ? "You don't" : "This user doesn't"} follow anyone yet...`}
        />
      ) : (
        <AnimatedList
          key="following-list"
          items={following}
          renderItem={(followed) => <FollowCard profile={followed} />}
          itemKeyFn={(followed) => followed.id}
        />
      )}
    </AnimatePresence>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex h-full w-full items-center justify-center pb-10"
    >
      <p className="select-none text-center text-sm font-[650] text-neutral-600">{message}</p>
    </motion.div>
  );
}
